import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { useState } from "react";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { toast } from "sonner";
import DotPattern from "@/components/dot-pattern";
import UserForm from "@/features/users/components/form/user-form";
import { userFormSchema } from "@/features/users/components/form/schemas";
import { usersRepo } from "@/lib/repositories";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { useDynamicPageTitle } from "@/hooks/useDynamicPageTitle";
import { useFetchUser } from "@/features/users/hooks/useFetchUser";
import { queryClient } from "@/lib/query-client";
import type { ApiUser } from "@/features/users/types";

type UsersEditFormContentProps = { user: ApiUser };

const UsersEditFormContent = ({ user }: UsersEditFormContentProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof userFormSchema>>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: user.email,
      username: user.username,
      first_name: user.first_name,
      last_name: user.last_name,
      status: user.status,
    },
  });

  async function onSubmit(data: z.infer<typeof userFormSchema>) {
    if (!id) return;
    setIsSubmitting(true);
    const [error, userData] = await catchError(usersRepo.patch(id, data));
    if (error) toast.error(handleApiError(error).message);
    if (userData) {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(t("messages.user_edit_success"));
      navigate(ROUTES.USERS);
    }
    setIsSubmitting(false);
  }

  return (
    <UserForm
      form={form}
      onSubmit={onSubmit}
      isLoading={isSubmitting}
      user={user}
    />
  );
};

const UsersEditPage = () => {
  const { id } = useParams();
  const { user, isLoading } = useFetchUser(id);
  useDynamicPageTitle(user ? `${user.reference}` : "");

  return (
    <div className="relative h-full flex flex-col items-center gap-y-3 p-3 py-4 sm:py-10 sm:p-6">
      <DotPattern />
      {isLoading && <Skeleton className="w-full h-137 md:h-80" />}
      {user && <UsersEditFormContent user={user} />}
    </div>
  );
};

export default UsersEditPage;
